import { User, MapPin, MessageSquare, Gift, Clock, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, lazy, Suspense } from 'react';
import { Address } from '../../types/api';
import { useTranslation } from 'react-i18next';

// Lazy load the modal to prevent initialization on import
const ScheduleSetupModal = lazy(() => import('../ScheduleSetupModal'));

interface RecipientInfoProps {
  recipientName: string;
  setRecipientName: (name: string) => void;
  recipientPhone: string;
  setRecipientPhone: (phone: string) => void;
  giftMessage: string;
  setGiftMessage: (message: string) => void;
  senderName?: string;
  setSenderName?: (name: string) => void;
  senderPhone?: string;
  setSenderPhone?: (phone: string) => void;
  isGift?: boolean;
  
  // Add props for delivery address and schedule
  selectedAddress?: Address | null;
  selectedTimeSlots?: string[];
  selectedDate?: string;
  weeklyDay?: number;
  navigateToAddressPage?: () => void;
  onScheduleUpdate?: (scheduleData: any) => void;
}

// Helper function to get recipient information from address
const getRecipientInfoFromAddress = (address: any, fieldType: 'name' | 'phone'): string => {
  if (!address) return '';
  
  if (fieldType === 'name') {
    // Use recipientName from direct input first, then try other fields
    if (address.recipientName) return address.recipientName;
    // Next priority is address name fields
    if (address.contactName) return address.contactName;
    if (address.name) return address.name;
    if (address.fullName) return address.fullName;
    return 'Recipient name (from delivery address)';
  } else {
    // Use recipientPhone from direct input first, then try other fields
    if (address.recipientPhone) return address.recipientPhone;
    if (address.contactPhone) return address.contactPhone;
    if (address.phone) return address.phone;
    if (address.phoneNumber) return address.phoneNumber;
    return 'Recipient phone (from delivery address)';
  }
};

const RecipientInfoSection = ({
  recipientName,
  setRecipientName,
  recipientPhone,
  setRecipientPhone,
  giftMessage,
  setGiftMessage,
  senderName = '',
  setSenderName = () => {},
  senderPhone = '',
  setSenderPhone = () => {},
  isGift = false,
  selectedAddress = null,
  selectedTimeSlots = [],
  selectedDate = '',
  weeklyDay = 1,
  navigateToAddressPage = () => {},
  onScheduleUpdate = () => {}
}: RecipientInfoProps) => {
  const navigate = useNavigate();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const { t } = useTranslation();
  // Format the display time (copied from AddressAndScheduleSection)
  const formatTimeSlot = (timeSlot: string): string => {
    if (!timeSlot) return 'Morning';
    if (timeSlot.includes('9:00 AM')) return 'Morning';
    if (timeSlot.includes('2:00 PM')) return 'Afternoon';
    return timeSlot;
  };
  
  const displayTime = selectedTimeSlots.length > 0 
    ? selectedTimeSlots
        .filter((slot: string | null): slot is string => typeof slot === 'string')
        .map(formatTimeSlot)
        .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index)
        .join(', ')
    : '';
  
  // Get day name based on weeklyDay number (0 = Sunday, 1 = Monday, etc.)
  const getDayName = (day: number | undefined) => {
    if (day === undefined) return t('subscription.addressAndSchedule.days.monday');
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return t(`subscription.addressAndSchedule.days.${dayKeys[day]}`);
  };
  
  const dayName = getDayName(weeklyDay);
  
  // Helper function to format address display (simplified from AddressAndScheduleSection)
  const formatAddressDisplay = () => {
    if (!selectedAddress) return "Add delivery address";
    
    if (typeof selectedAddress === 'object' && selectedAddress !== null) {
      // Try to extract the most meaningful address information
      if ('address' in selectedAddress && typeof selectedAddress.address === 'string') {
        return selectedAddress.address;
      } else if ('fullAddress' in selectedAddress && typeof selectedAddress.fullAddress === 'string') {
        return selectedAddress.fullAddress;
      } else if ('street' in selectedAddress) {
        const parts: string[] = [];
        const addr = selectedAddress as any;
        if (addr.street) parts.push(addr.street);
        if (addr.city) parts.push(addr.city);
        if (addr.state) parts.push(addr.state);
        if (addr.zipCode) parts.push(addr.zipCode);
        return parts.join(', ');
      } else {
        // If we can't find standard address fields, show all non-id fields
        const addressParts = Object.entries(selectedAddress)
          .filter(([key, value]) => 
            !key.toLowerCase().includes('id') && 
            typeof value === 'string' && 
            value.length > 0
          )
          .map(([_, value]) => value as string);
        
        return addressParts.length > 0 
          ? addressParts.join(', ') 
          : JSON.stringify(selectedAddress);
      }
    } else if (typeof selectedAddress === 'string') {
      return selectedAddress;
    } else {
      return 'Address selected (unknown format)';
    }
  };
  
  const handleScheduleModalOpen = () => {
    setShowScheduleModal(true);
  };

  const handleScheduleModalClose = () => {
    setShowScheduleModal(false);
  };

  const handleScheduleSave = (scheduleData: any) => {
    if (onScheduleUpdate) {
      onScheduleUpdate(scheduleData);
    }
    setShowScheduleModal(false);
  };
  
  // Only show the gift subscription UI when isGift is true
  if (isGift) {
    return (
      <div>
        {/* Sender Section */}
        <div className="mb-6">
          <h3 className="text-base font-medium text-gray-900 mb-4">{t('subscription.recipientInfo.sender')}</h3>
          
          <div className="space-y-4">
            <div className="flex items-center border-b border-gray-200 pb-3">
              <User size={20} className="text-gray-400 mr-3" />
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className="w-full p-0 border-none focus:outline-none focus:ring-0"
                placeholder={t('subscription.recipientInfo.nameRequired')}
                required
              />
            </div>
            
            <div className="flex items-center border-b border-gray-200 pb-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 mr-3">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <input
                type="tel"
                value={senderPhone}
                maxLength={10}
                onChange={(e) => {
                  if(e.target.value.match(/^\d+$/) || e.target.value == '') {
                    setSenderPhone(e.target.value);
                  }
                }}
                className="w-full p-0 border-none focus:outline-none focus:ring-0"
                placeholder={t('subscription.recipientInfo.phoneRequired')}
                required
              />
            </div>
          </div>
        </div>

        {/* Recipient Section */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">{t('subscription.recipientInfo.recipient')}</h3>
          
          <div className="space-y-4">
            {/* Delivery Address - clickable */}
            <div 
              className="flex items-center border-b border-gray-200 pb-3 cursor-pointer"
              onClick={navigateToAddressPage}
            >
              <MapPin size={20} className="text-gray-400 mr-3" />
              <div className="w-full flex items-center justify-between">
                <span className={selectedAddress ? "text-gray-700" : "text-gray-500"}>
                  {selectedAddress ? formatAddressDisplay() : t('subscription.addressAndSchedule.deliveryAddress')}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            </div>
            
            {/* Delivery Schedule - clickable */}
            <div 
              className="flex items-center border-b border-gray-200 pb-3 cursor-pointer"
              onClick={handleScheduleModalOpen}
            >
              <Clock size={20} className="text-gray-400 mr-3" />
              <div className="w-full flex items-center justify-between">
                <span className="text-gray-700">
                  {t('subscription.addressAndSchedule.every')} <span className="font-medium">{dayName}</span> • {displayTime}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            </div>
            
            {/* Recipient name - read-only, displays from address */}
            <div className="flex items-center border-b border-gray-200 pb-3">
              <User size={20} className="text-gray-400 mr-3" />
              <div className="w-full text-gray-700">
                {getRecipientInfoFromAddress(selectedAddress, 'name')}
              </div>
            </div>
            
            {/* Recipient phone - read-only, displays from address */}
            <div className="flex items-center border-b border-gray-200 pb-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 mr-3">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <div className="w-full text-gray-700">
                {getRecipientInfoFromAddress(selectedAddress, 'phone')}
              </div>
            </div>
            
            <div className="flex items-start border-b border-gray-200 pb-3">
              <Gift size={20} className="text-gray-400 mr-3 mt-1" />
              <input
                type="text"
                value={giftMessage}
                onChange={(e) => setGiftMessage(e.target.value)}
                className="w-full p-0 border-none focus:outline-none focus:ring-0"
                placeholder={t('subscription.recipientInfo.giftMessage')}
              />
            </div>
          </div>
        </div>
      
        {/* Only render ScheduleSetupModal when it's opened */}
        {showScheduleModal && (
          <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-white p-4 rounded-lg shadow-xl">Loading schedule options...</div>
          </div>}>
            <ScheduleSetupModal
              isOpen={showScheduleModal}
              onClose={handleScheduleModalClose}
              initialData={{
                limitedView: true,
                fromSubscription: true,
                selectedAddress: selectedAddress,
                selectedTimeSlots: selectedTimeSlots,
                selectedDate: selectedDate ? new Date(selectedDate) : undefined,
                weeklyDay: weeklyDay,
                isWeeklySubscription: true,
                returnTo: '/subscription',
                giftType: 'direct',
                recipientName: getRecipientInfoFromAddress(selectedAddress, 'name'),
                recipientPhone: getRecipientInfoFromAddress(selectedAddress, 'phone'),
                giftMessage,
                senderName,
                senderPhone
              }}
              onSave={handleScheduleSave}
            />
          </Suspense>
        )}
      </div>
    );
  }
  
  // Original UI for non-gift subscription
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <User size={20} className="text-gray-500 mr-2" />
          <span className="text-base font-medium text-gray-900">Recipient Information</span>
        </div>
      </div>
      
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipient's Name
          </label>
          <input
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F9D348] focus:border-[#F9D348] transition-colors"
            placeholder="Enter recipient's name"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipient's Phone Number
          </label>
          <input
            type="tel"
            value={recipientPhone}
            onChange={(e) => setRecipientPhone(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F9D348] focus:border-[#F9D348] transition-colors"
            placeholder="Enter recipient's phone number"
            required
          />
          <p className="text-xs text-gray-500 mt-2 ml-1">
            The recipient will use this phone number to access their gift subscription
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gift Message (Optional)
          </label>
          <textarea
            value={giftMessage}
            onChange={(e) => setGiftMessage(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F9D348] focus:border-[#F9D348] transition-colors min-h-[100px] resize-none"
            placeholder="Add a personal message for the recipient"
          />
        </div>
      </div>
    </div>
  );
};

export default RecipientInfoSection;